<template>
  <div>
    <div
      :id="`productCriteria_${$props.product.productId}_${props.person.number}`"
      :data-product-id="$props.product.productId"
      class="product"
    >
      <div class="product__actions">
        <slot name="actions">
          <basic-text-button
            v-if="isProductEditAvailable"
            :label="$t('form.edit')"
            @click="productCriteriaModalRef.modalRef.open()"
          >
            <template #trailing>
              <the-icon
                art="solid"
                name="pencil"
              />
            </template>
          </basic-text-button>
          <basic-text-button
            v-if="isProductEditable"
            :label="$t('product.actions.remove')"
            @click="removeProduct($props.product.productId)"
          >
            <template #trailing>
              <the-icon
                art="solid"
                name="trash-can-xmark"
              />
            </template>
          </basic-text-button>
        </slot>
      </div>

      <div
        class="product__name body-2"
        v-bind="$attrs"
        v-html="productTitle"
      />

      <div
        class="product__designation h3"
        :data-product-id="$props.product.productId"
        v-html="$te(`products.${$props.product.productId}.designation`) ? $t(`products.${$props.product.productId}.designation`) : null"
      />

      <basic-product-pricing
        class="product__pricing"
        price-class="lead-1"
        suffix-class="lead-2"
        :product="$props.product"
        :show-zero-value="$props.product.price.reduction < 0"
        :suffix="$t('product.currency')"
      />

      <div
        v-if="(isProductEditAvailable && (errors.length > 0 || isProductNotDeletable)) || $props.messages.length > 0"
        class="product__messages"
      >
        <div
          v-if="$props.messages.length > 0"
          class="product__message"
        >
          <basic-message
            v-for="message in $props.messages"
            :key="message.contentKey"
            :closeable="false"
            :severity="message.severity"
            :content="message.contentKey ? $t(message.contentKey) : null"
            :title-label="message.titleKey ? $t(message.titleKey) : null"
          />
        </div>
        <div
          v-if="isProductNotDeletable"
          class="product__message"
        >
          <basic-message
            :severity="NOTIFICATION.WARNING"
            :content="$t('product.detail.cancellation.message')"
          />
        </div>
        <div
          v-if="errors.length > 0"
          class="product__message"
        >
          <basic-message
            v-for="error in errors"
            :key="error.key"
            :closeable="false"
            :actions="
              // @note: AiAiAide shouldn't show any actions here, as it's not possible to see a product without a doctor
              !route?.meta?.aide ?
                [
                  {
                    label: error.key === 'DOCTOR_MISSING_WARNING_CODE' ? $t('product.actions.add') : $t('product.toOffer'),
                    callback: () => {
                      if (error.key === 'DOCTOR_MISSING_WARNING_CODE') {
                        const eventName = FEATURE_FLAG_CARE_PROVIDER_MODAL
                          ? EVENT_CARE_PROVIDER_MODAL.OPEN
                          : EVENT_MEDICAL_OFFICE_MODAL_OPEN
                        events$.emit(eventName, {
                          personId: props.person.personId,
                          product: props.product,
                          callback: async data => {
                            loading$.start()

                            try {
                              const hasDoctorChanged = !!data.doctor
                              await addProductWithDoctor({
                                categoryId: props.categoryId,
                                doctor: hasDoctorChanged ? data.doctor : data,
                                personId: props.person.personId,
                                productId: hasDoctorChanged ? data.productId : props.product.productId,
                                source: props.source,
                              })

                              loading$.end()
                            } catch {
                              loading$.failed()
                            }
                          },
                        })
                      } else {
                        const routeName = ROUTE_NAME.PRODUCT_CATEGORY
                        if (route.name !== routeName) {
                          router.push({
                            name: routeName,
                            params: {
                              categoryId: props.categoryId,
                              personId: props.person.personId,
                            },
                          })
                        }
                      }
                    },
                  },
                ] : []"
            :content="$t(`error.${error.key}`)"
            :severity="error.type.toLowerCase()"
          />
        </div>
      </div>

      <div class="product__options">
        <product-options
          v-if="$props.categoryId && groupId"
          mode="read"
          :category-id="$props.categoryId"
          :group-id="groupId"
          :person="$props.person"
          :product="$props.product"
          :show-doctor-reduction="false"
          :source="$props.source"
          @change="event => emits('change', event)"
        />
      </div>

      <product-upsell
        v-if="hasUpsellProduct"
        class="product__upsell"
        :category-id="$props.categoryId"
        :person="$props.person"
        :person-id="$props.person.personId"
        :product="$props.product"
        :upsell-product="upsellProduct"
        @add="data => $emit('add-product', data)"
        @open="upsellProductModalRef.modalRef.open()"
      />

      <basic-product-price
        class="product__price"
        price-class="lead-1"
        suffix-class="lead-2"
        :product="$props.product"
        :show-zero-value="$props.product.price.reduction < 0"
        :suffix="$t('product.currency')"
        reduction-as-icon
      />
      <div
        v-if="$slots.default"
        class="product__slot"
      >
        <slot />
      </div>
    </div>

    <dvp-product-modal
      v-if="isProductEditAvailable && groupId"
      :id="`productCriteriaModal_${$props.product.productId}_${props.person.number}`"
      ref="productCriteriaModalRef"
      :category-id="$props.categoryId"
      :group-id="groupId"
      :person="$props.person"
      :product="$props.product"
      :source="$props.source"
    />

    <dvp-product-modal
      v-if="upsellProduct && groupId"
      :id="`upsellModal_${upsellProduct.productId}`"
      ref="upsellProductModalRef"
      :category-id="$props.categoryId"
      :group-id="groupId"
      :person="$props.person"
      :product="upsellProduct"
      :source="SOURCE.UPSELLING"
    />
  </div>
</template>

<script setup>
  import { computed, reactive, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import { logWarning } from '@shared/helpers/Logger'

  import { events$, loading$ } from '@shared/services'
  import { ReactivityUtil } from '@shared/utils/Reactivity'
  import { isProductReadOnlyHaX } from '@shared/utils/HaX'

  import useApplication from '@shared/hooks/useApplication'
  import useI18n from '@shared/hooks/useI18n'
  import useProduct from '@shared/hooks/useProduct'
  import useProductPersons from '@shared/hooks/useProductPersons'
  import useProductState from '@shared/hooks/useProductState'

  import ProductUpsell from '@shared/components/Product/ProductUpsell'
  import ProductOptions from '@shared/components/Product/Options'
  import DvpProductModal from '@dvp/components/ProductModal'

  import { BasicMessage, BasicProductPrice, BasicProductPricing, BasicTextButton } from '@shared/components/Basic'

  import { NOTIFICATION, PRODUCT_STATE, ROUTE_NAME } from '@shared/config/constants'
  import { EVENT_CARE_PROVIDER_MODAL, EVENT_MEDICAL_OFFICE_MODAL_OPEN, SOURCE } from '@shared/config/events'

  // HOOKS
  const { isWww } = useApplication()
  const route = !isWww.value ? useRoute() : {}
  const router = !isWww.value ? useRouter() : {}
  const { t, te } = useI18n()
  const { getGroupIdFromProduct, isUpgrade } = useProduct()
  const { addProductWithDoctor } = useProductPersons()
  const { state } = useProductState()

  // INIT
  const emit = defineEmits(['add-product', 'change-product', 'remove-product'])
  const props = defineProps({
    categoryId: {
      type: String,
      required: true,
    },

    groupId: {
      type: String,
      default: '',
    },

    isMobileView: {
      type: Boolean,
      default: false,
    },

    messages: {
      type: Array,
      default: () => [],
      validator(val) {
        if (val.length === 0) return true
        const isValidObj = val.every(obj => Object.hasOwn(obj, 'severity') && (Object.hasOwn(obj, 'titleKey') || Object.hasOwn(obj, 'contentKey')))
        if (!isValidObj) {
          logWarning('[Vue Warn]: Invalid prop: items should have the following properties: {severity, titleKey|contentKey}')
          return false
        }
        return true
      },
    },

    person: {
      type: Object,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },

    source: {
      type: String,
      default: SOURCE.BASKET,
    },

    upselling: {
      type: Boolean,
      default: true,
    },
  })

  // DATA
  const upsellProductModalRef = ref({})
  const productCriteriaModalRef = ref()
  const upsellProduct = reactive({})
  const hasUpsellProduct = ref(false)
  const FEATURE_FLAG_CARE_PROVIDER_MODAL = import.meta.env.VITE_FEATURE_FLAG_CARE_PROVIDER_MODAL

  // COMPUTED
  const errors = computed(() => {
    if (!props.product.valid) {
      return props.product.messages.filter(m => m.type !== 'info')
    } else {
      return []
    }
  })

  const groupId = computed(() => {
    return props.groupId || getGroupIdFromProduct(props.product.productId)
  })

  const hasProductOptions = computed(() => {
    const { hasOptions } = useProduct()
    return hasOptions(props.product)
  })

  const isProductEditable = computed(() => {
    return productState.value === PRODUCT_STATE.EDITABLE && !isProductReadOnlyHaX(props.product.productId)
  })

  const isProductEditAvailable = computed(() => {
    return (
      !isProductReadonly.value &&
      hasProductOptions.value &&
      !isUpgrade(props.product.productId) &&
      options.value &&
      !isProductReadOnlyHaX(props.product.productId)
    )
  })

  const isProductNotDeletable = computed(() => {
    return productState.value === PRODUCT_STATE.NOT_DELETABLE || isProductReadOnlyHaX(props.product.productId)
  })

  const isProductReadonly = computed(() => {
    return productState.value === PRODUCT_STATE.READONLY
  })

  const options = computed(() => {
    const { composeOptions } = useProduct()

    return composeOptions(props.product, groupId.value, props.categoryId)
  })

  const productState = computed(() => {
    if (props.readOnly) return PRODUCT_STATE.READONLY
    return state(props.person.partnerNumber, props.product.productId)
  })

  const productTitle = computed(() => {
    const titleKey = `products.${props.product.productId}`

    if (te(titleKey, 'products')) {
      return t(`${titleKey}.name`)
    } else if (props.product.label) {
      return props.product.label
    } else {
      return null
    }
  })

  // METHODS
  function removeProduct(productId) {
    emit('remove-product', {
      productId,
      personId: props.person.personId,
      categoryId: props.categoryId,
    })
  }

  // WATCHERS
  watch(
    () => props.person.selectedProducts,
    () => {
      if (props.upselling && props.product.upSelling) {
        const selected = !!props.person.selectedProducts.find(
          __product => __product.productId === props.product.upSelling
        )
        const isValidUpsell = props.person.products.products[props.product.upSelling].valid

        if (!selected && isValidUpsell) {
          const _upsellProduct = ReactivityUtil.clone(
            Object.assign({}, props.person.products.products[props.product.upSelling], {
              productId: props.product.upSelling,
            })
          )
          ReactivityUtil.reAssign(upsellProduct, _upsellProduct)
          hasUpsellProduct.value = true
        } else {
          hasUpsellProduct.value = false
        }
      }
    },
    { immediate: true }
  )
</script>

<style scoped>
.product {
  padding: var(--dotcom-responsive-spacing-res-s);
  border-radius: var(--fixed-border-radius-fix-03);
  border: 1px solid var(--outline-variant);
  display: grid;
  grid-template-columns: 1fr;
  column-gap: var(--fixed-spacing-fix-04);
  grid-template-areas:
    'name'
    'designation'
    'pricing'
    'options'
    'upsell'
    'messages'
    'price'
    'actions'
    'slot';
}

.product__name {
  grid-area: name;
  margin: 0 0 var(--fixed-spacing-fix-02);
}

.product__designation {
  grid-area: designation;
}

.product__pricing {
  grid-area: pricing;
}

.product__content {
  grid-area: content;
  margin: var(--dotcom-responsive-spacing-res-xxs) 0 0;
}

.product__options {
  grid-area: options;
  margin: var(--fixed-spacing-fix-02) 0 0;
}

.product__pricing {
  margin: var(--fixed-spacing-fix-02) 0 0;
}

.product__upsell {
  grid-area: upsell;
  margin: var(--fixed-spacing-fix-03) 0 0;
}

.product__messages {
  grid-area: messages;
}

.product__message {
  margin: var(--dotcom-responsive-spacing-res-xxs) 0 0;
  max-width: 500px;
}

.product__price {
  margin: var(--dotcom-responsive-spacing-res-xxs) 0 0;
  grid-area: price;
  place-self: end end;
}

.product__actions {
  grid-area: actions;
  justify-self: end;
  display: flex;
  flex-direction: row;
  column-gap: var(--fixed-spacing-fix-06);
  margin: var(--fixed-spacing-fix-02) 0 0;
}

.product__slot {
  grid-area: slot;
}

@media (--v-medium) {
  .product {
    grid-template-columns: max-content 1fr max-content;
    grid-template-areas:
      'name actions actions'
      'designation designation designation'
      'pricing pricing pricing'
      'options options options'
      'upsell upsell upsell'
      'messages messages price'
      'slot slot slot';
  }

  .product__actions,
  .product__price {
    margin: 0;
  }
}
</style>
